import { ModalCenter, type ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { resolveApiAssetUrl } from '@shared/utils/resolveApiAssetUrl';
import { cn } from '@shared/utils/utils';
import { faFileLines } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { forwardRef, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { OrderAttachment } from '../../models/Order';
import { isImageAttachment, isPdfAttachment } from '../../utils/attachmentPreview';

interface AttachmentsModalProps {
  attachments: OrderAttachment[];
}

export const AttachmentsModal = forwardRef<ModalCenterRef, AttachmentsModalProps>(({ attachments }, ref) => {
  const { t } = useTranslation('viewOrdersModule');
  const [selectedAttachmentId, setSelectedAttachmentId] = useState<number | null>(null);

  const selectedAttachment =
    attachments.find((attachment) => attachment.id === selectedAttachmentId) ?? attachments[0] ?? null;

  const previewUrl = useMemo(() => resolveApiAssetUrl(selectedAttachment?.url), [selectedAttachment?.url]);

  useEffect(() => {
    if (attachments.length > 0) {
      setSelectedAttachmentId(attachments[0].id);
    }
  }, [attachments]);

  const renderPreview = () => {
    if (!selectedAttachment) {
      return <p className="text-sm text-complementary-text">{t('modals.attachments.empty')}</p>;
    }

    if (!previewUrl) {
      return (
        <div className="flex h-full min-h-[75vh] items-center justify-center">
          <p className="text-sm text-subtext">{t('modals.attachments.preview-error')}</p>
        </div>
      );
    }

    if (isImageAttachment(selectedAttachment.name)) {
      return (
        <div className="flex h-full min-h-[75vh] items-center justify-center overflow-auto p-2">
          <img
            src={previewUrl}
            alt={selectedAttachment.name}
            className="max-h-[85vh] max-w-full rounded-md object-contain"
            data-testid="attachment-preview-image"
          />
        </div>
      );
    }

    if (isPdfAttachment(selectedAttachment.name)) {
      return (
        <iframe
          key={previewUrl}
          src={previewUrl}
          title={selectedAttachment.name}
          className="h-[85vh] w-full rounded-md border border-card-border bg-card-bg"
          data-testid="attachment-preview-pdf"
        />
      );
    }

    return (
      <iframe
        key={previewUrl}
        src={previewUrl}
        title={selectedAttachment.name}
        className="h-[85vh] w-full rounded-md border border-card-border bg-card-bg"
        data-testid="attachment-preview-file"
      />
    );
  };

  return (
    <ModalCenter
      ref={ref}
      title={t('modals.attachments.title', { count: attachments.length })}
      width="xxl"
      type="default"
      fullScreenMobile={true}
      className="sm:!w-[min(1280px,95vw)]"
      maxHeight="95dvh"
      data-testid="attachments-modal"
    >
      <div className="flex flex-col gap-4 py-2 md:flex-row md:gap-6">
        <ul
          className="flex shrink-0 flex-row gap-2 overflow-x-auto md:w-64 md:flex-col md:overflow-y-auto md:overflow-x-hidden md:max-h-[85vh]"
          data-testid="attachments-list"
        >
          {attachments.map((attachment) => {
            const isSelected = attachment.id === selectedAttachment?.id;

            return (
              <li key={attachment.id}>
                <button
                  type="button"
                  onClick={() => setSelectedAttachmentId(attachment.id)}
                  className={cn(
                    'flex w-full min-w-[180px] items-center gap-2 rounded-md border px-3 py-2 text-left transition-colors md:min-w-0',
                    isSelected
                      ? 'border-primary bg-hover-press text-title'
                      : 'border-card-border bg-card-bg text-complementary-text hover:bg-hover-press hover:text-title',
                  )}
                  data-testid={`attachment-item-${attachment.id}`}
                >
                  <FontAwesomeIcon icon={faFileLines} className="!h-3 !w-3 shrink-0" />
                  <span className="truncate text-sm">{attachment.name}</span>
                </button>
              </li>
            );
          })}
        </ul>

        <div className="min-w-0 flex-1">{renderPreview()}</div>
      </div>
    </ModalCenter>
  );
});

AttachmentsModal.displayName = 'AttachmentsModal';
